import type { Player } from '../../domain/types';
import { seatPositionPercent, seatRotationDeg } from './boardLayout';
import { DiceFace } from './DiceFace';

interface PlayerSeat2DProps {
  player: Player;
  playerCount: number;
  isActive: boolean;
  isDealer: boolean;
  isOverview: boolean;
  isBotActing: boolean;
}

export function PlayerSeat2D({
  player,
  playerCount,
  isActive,
  isDealer,
  isOverview,
  isBotActing,
}: PlayerSeat2DProps) {
  const position = seatPositionPercent(player.index, playerCount);
  const rotation = seatRotationDeg(player.index, playerCount);
  const highlight = isActive || isBotActing;
  const diceSize = isOverview ? 22 : 26;

  return (
    <div
      className={`board-seat absolute ${
        isBotActing ? 'animate-pulse ring-2 ring-amber-400/70 ring-offset-2 ring-offset-transparent' : ''
      }`}
      style={{
        left: position.left,
        top: position.top,
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
      }}
    >
      <div
        className={`flex w-[9.5rem] flex-col items-center rounded-xl border px-2 py-2 ${
          player.isHuman
            ? 'border-sky-400/40 bg-sky-950/60'
            : highlight
              ? 'border-amber-400/50 bg-amber-950/40'
              : 'border-white/10 bg-black/40'
        }`}
        style={{ transform: `rotate(${-rotation}deg)` }}
      >
        <p
          className={`mb-1 max-w-full truncate text-center font-semibold ${
            highlight ? 'text-amber-200' : 'text-white/90'
          } ${isOverview ? 'text-sm' : 'text-xs'}`}
        >
          {player.name}
          {isDealer && <span className="ml-1 text-[10px] text-amber-300">●</span>}
        </p>

        <div className="mb-1 flex gap-0.5 rounded-lg bg-black/30 p-1">
          {player.board.map((value, slotIndex) => (
            <div
              key={slotIndex}
              className="flex h-7 w-7 items-center justify-center rounded border border-white/10 bg-white/5"
            >
              {value !== null ? <DiceFace value={value} size={diceSize} /> : null}
            </div>
          ))}
        </div>

        {player.tray.length > 0 && (
          <div className="mb-1 flex flex-wrap justify-center gap-0.5">
            {player.tray.map((value, index) => (
              <DiceFace key={`tray-${player.index}-${index}`} value={value} size={diceSize - 2} />
            ))}
          </div>
        )}

        <div className="flex w-full items-center justify-between text-[10px] text-white/70">
          <span>칩 {player.chips}</span>
          {player.betAmount > 0 && <span className="text-amber-300">베팅 {player.betAmount}</span>}
        </div>
      </div>
    </div>
  );
}
